/** Label + control + optional hint.
 *
 *  A module-level component on purpose, and the reason is a crash the old NewClient dialog hit:
 *  a component *defined during render* gets a new identity every pass, so the control inside it
 *  unmounts and remounts on every keystroke, and the stylis-plugin-rtl emotion cache re-inserting
 *  its `<style>` nodes then throws `NotFoundError: insertBefore`. That dialog worked around it by
 *  keeping its shared sub-renders as plain called functions. Declared at module scope the identity
 *  is stable instead, so this can be a normal component and the steps can use it as one.
 *
 *  The rule that matters for anything added here: never create a component during render. */

import type { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { t } from "../../dashboard/coach/coachTokens";
import { hintSx, labelSx } from "./wizardStyles";

interface WizardFieldProps {
  label: string;
  children: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  /** Field-level validation message; replaces the hint and recolours the label. */
  error?: string | null;
}

const WizardField = ({ label, children, required, hint, error }: WizardFieldProps) => (
  <Box>
    <Typography sx={{ ...(labelSx as object), color: error ? t.redText : t.t2 }}>
      {label}
      {required && (
        <Box component="span" aria-hidden sx={{ color: t.red, marginInlineStart: "3px" }}>
          *
        </Box>
      )}
    </Typography>
    {children}
    {(error || hint) && (
      <Typography sx={{ ...(hintSx as object), color: error ? t.redText : t.t3 }}>
        {error || hint}
      </Typography>
    )}
  </Box>
);

export default WizardField;
